import React, {useState, useEffect, useContext, useCallback, useRef, useMemo} from 'react';
import './css/PortfolioGrid.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {fetchPortfolioInstruments} from './PortfolioService';
import WebSocketService from "../../websockets/WebSocketService";
import {updateTickerSubscriptionWithKite, getExactInstrument} from "../services/apiService";
import StockPopup from './popups/StockPopup';
import {useRefreshContext} from "./context/RefreshContext"; // Import the context
import {DataContext} from "../contexts/DataContext";
import {useSharingContext} from "../contexts/SharingContext"; // ✅ Correct import
import {normalizeSymbol} from "@dashboard/shared/pfSymbolsRef";
import {getLastClosePrice, getLastPrice} from "@portfolio/utils/PFService";
import {tickStorage} from "@dashboard/contexts/tickStore";
import {exportToCSV, exportToExcel, syncPortfolioExport} from '@portfolio/utils/portfolioExportUtils';
import StockTransactionsPopup from "@portfolio/popups/ClientStockTransactionsPopup";
import PortfolioHeaderTable from "@portfolio/PFGridComponents/PortfolioHeaderTable";
import PortfolioTableBody from "@portfolio/PFGridComponents/PortfolioTableBody";
import PortfolioUtilButtons from "@portfolio/PFGridComponents/PortfolioUtilButtons";
import PortfolioTotalsProvider from "@portfolio/context/PortfolioTotalsProvider";
import {RowSelectionProvider} from "@portfolio/context/RowSelectionContextProvider";
import {assignPFSymbols, pfSymbolsRef} from "@dashboard/shared/pfSymbolsRef";

const ROW_HEIGHT = 50;

function downloadJSON(obj, filename) {
    const json = JSON.stringify(obj, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
}

function hashStocksSnapshot(stocksArr, tabKey) {
    //console.log(tabKey, stocksArr);
    const norm = (stocksArr || [])
        .map(x => ({
            s: (x.symbol ?? x.stockSymbol ?? '').toString(),
            q: Number(x.netQuantity ?? 0),
            a: Number(x.weightedAvgPrice ?? 0)
        }))
        // sort to make hash independent of row order
        .sort((A, B) => A.s.localeCompare(B.s) || A.q - B.q || A.a - B.a);

    const s = JSON.stringify({
        t: String(tabKey ?? ''), // stable per-client tab key
        n: norm
    });

    let h = 5381;
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
    return h >>> 0;
}

function AutoExportOnRowChange({ stocks, quotes, tab, ready,debounceMs = 1200 }) {
    const stocksRef = useRef(stocks);
    const quotesRef = useRef(quotes);
    const tabRef    = useRef(tab);

    const timerRef = useRef(null);
    const scheduledSnapshotRef = useRef(null); // snapshot currently scheduled to export

    useEffect(() => { stocksRef.current = stocks; }, [stocks]);
    useEffect(() => { quotesRef.current = quotes; }, [quotes]);
    useEffect(() => { tabRef.current = tab; }, [tab]);

    useEffect(() => {
        if (!ready) return;          // ⬅️ don’t hash during the transient render
        if (!stocks || !stocks.length) return;

        const snapshot = hashStocksSnapshot(stocks, tab);
        const tabKey = String(tab ?? "default");
        const storageKey = `pfExport:lastHash:${tabKey}`;

        // If we already exported this snapshot this session, skip
        const lastPersisted = sessionStorage.getItem(storageKey);
        if (lastPersisted && String(snapshot) === lastPersisted) return;

        // If we already have a timer scheduled for this SAME snapshot, let it fire
        if (timerRef.current && scheduledSnapshotRef.current === snapshot) {
            return;
        }

        // If a different snapshot was scheduled, cancel that timer and schedule the new one
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        scheduledSnapshotRef.current = snapshot;

        timerRef.current = setTimeout(async () => {
            try {
                await syncPortfolioExport(stocksRef.current, quotesRef.current, tabRef.current);
                sessionStorage.setItem(storageKey, String(snapshot)); // remember per-tab
            } catch (e) {
                console.error("Export sync failed", e);
            } finally {
                // timer completed; clear booking
                timerRef.current = null;
                scheduledSnapshotRef.current = null;
            }
        }, debounceMs);
    }, [stocks, tab, ready, debounceMs]); // still omit quotes to avoid tick-trigger spam

    // Only clear on UNMOUNT to avoid canceling your in-flight debounce on regular re-renders

    return null;
}

// === End auto export helper ===

/*
* The isDataContextLoaded would trigger the useEffect that runs fetchPortfolioInstruments. it fetches all "stocks" from the backend. they would be entries from each contract notes. this funcion would also separate
* transactions from incoming stocks from nackend for each client and Group and save them in stocksPerClient. then it would do setStocks(mergedStocks) (mergedstocks=stocks of all clients i.e., group).
* This call would trigger sortStocks function because a useEffect with stocks dependecy calls it.
*
* SortStocks would take stocks of each client from stocksPerClient, call aggregateStocks to aggregate all entries of single symbol and derive single row to be displayed in portfolio table per stock for
* that client. then these aggregated arrays will also be sorted by currentValue, and saved into aggregatedSortedStocksPerClient for each client as well as the whole Group.
* sortStocks would then do setAggregatedSortedStocksCurrentClientTab. This will trigger useeffects depending on aggregatedSortedStocksCurrentClientTab, which will calculate initial layouts
* and hence table of portfolio will be rendered
**
* Then when user clicks on another client tab, the useEffect depending on value of activeTab would get triggered and do the setAggregatedSortedStocksCurrentClientTab from values of already agregated and sorted and
* saved aggregatedSortedStocksPerClient. this setAggregatedSortedStocksCurrentClientTab would trigger useeffects depending on aggregatedSortedStocksCurrentClientTab, which will calculate initial layouts
* and hence table of portfolio will be re-rendered
* */

const PortfolioGrid = () => {
    const tabID = 11;
    const [selectedStockTransactionsAcrossClients, setSelectedStockTransactionsAcrossClients] = useState(null);
    const [aggregatedSortedStocksCurrentClientTab, setAggregatedSortedStocksCurrentClientTab] = useState({}); // Cached stocks per client
    const [layouts, setLayouts] = useState([]);
    const [aggregatedSortedStocksPerClient, setAggregatedSortedStocksPerClient] = useState({}); // Cached stocks per client
    const {pfQuotes, setPfQuotes} = useSharingContext();
    const rowWrapperRef = useRef(null);
    const [stocksPerClient, setStocksPerClient] = useState({}); // Cached stocks per client
    const [selectedSymbol, setSelectedSymbol] = useState(null); // 🔹 Store clicked symbol
    const [sortConfig, setSortConfig] = useState({key: "currentValue", direction: "desc"});
    const [showSortDropdown] = useState(false);
    const {allInstrumentsList, initialSymbolToNSEAnnouncementsMap,initialSymbolToBSEAnnouncementsMap, isDataContextLoaded} = useContext(DataContext);

// ---------------------------
// Auto-refresh / freeze control
// ---------------------------
    const [isAutoRefreshing, setIsAutoRefreshing] = useState(true);
    const isAutoRefreshingRef = useRef(true);

    const [announcementStocksOnly, setAnnouncementStocksOnly] = useState(false);
    const announcementStocksOnlyRef = useRef(false);

// When we freeze (announcement-only mode), we render a snapshot for 4 minutes
    const frozenSnapshotRef = useRef({ active: false, untilTs: 0, stocks: null, layouts: null });

    // Keep track of what is CURRENTLY rendered (so we can freeze it even if React re-renders)
    const latestDisplayStocksRef = useRef([]);
    const latestDisplayLayoutsRef = useRef([]);

    const pauseTimeoutRef = useRef(null);

    // When we pause but need one UI change to happen first (e.g. opening announcements),
    // we schedule the freeze to happen AFTER the next render paints the change.
    const pendingFreezeRef = useRef({ active: false, untilTs: 0 });

    const clearPauseTimer = () => {
        if (pauseTimeoutRef.current) {
            clearTimeout(pauseTimeoutRef.current);
            pauseTimeoutRef.current = null;
        }
    };

    const resumeAutoRefreshNow = useCallback((triggerRefresh = false) => {
        clearPauseTimer();
        pendingFreezeRef.current = { active: false, untilTs: 0 };
        frozenSnapshotRef.current = { active: false, untilTs: 0, stocks: null, layouts: null };
        announcementStocksOnlyRef.current = false;
        setAnnouncementStocksOnly(false);

        isAutoRefreshingRef.current = true;
        setIsAutoRefreshing(true);

        if (triggerRefresh) {
            setPfGridrefreshKey(oldKey => oldKey + 1);
        }
    }, []);

    const pauseAutoRefreshForMs = useCallback((ms, { triggerRefreshOnResume = true, freezeImmediately = true, thawExistingFreeze = false } = {}) => {
        // clamp to 4 minutes max
        const clamped = Math.min(ms ?? 0, 4 * 60 * 1000);

        // If we're entering NOT-REFRESHING, we usually freeze what the user is currently seeing.
// However, for some actions (like opening the announcement panel) we want to let that UI
// change render first, then freeze right after it paints.
        if (clamped > 0) {
            const now = Date.now();
            const existing = frozenSnapshotRef.current;

            // If we're already frozen (e.g. Announcement-stocks-only mode) and we want to let
            // a UI change (like opening AnnouncementList) render first, we must temporarily thaw
            // the snapshot so that the state update is visible, and then refreeze on the next paint.
            if (!freezeImmediately && thawExistingFreeze && existing?.active) {
                frozenSnapshotRef.current = { active: false, untilTs: 0, stocks: null, layouts: null };
            }

            if (existing?.active) {
                // Extend freeze window if this pause lasts longer
                frozenSnapshotRef.current = {
                    ...existing,
                    untilTs: Math.max(existing.untilTs ?? 0, now + clamped),
                };
            } else if (freezeImmediately) {
                frozenSnapshotRef.current = {
                    active: true,
                    untilTs: now + clamped,
                    stocks: Array.isArray(latestDisplayStocksRef.current) ? latestDisplayStocksRef.current : [],
                    layouts: Array.isArray(latestDisplayLayoutsRef.current) ? latestDisplayLayoutsRef.current : [],
                };
            } else {
                // Defer freeze until after the next render
                pendingFreezeRef.current = { active: true, untilTs: now + clamped };
            }
        }

        clearPauseTimer();

        isAutoRefreshingRef.current = false;
        setIsAutoRefreshing(false);

        if (clamped > 0) {
            pauseTimeoutRef.current = setTimeout(() => {
                resumeAutoRefreshNow(triggerRefreshOnResume);
            }, clamped);
        }
    }, [resumeAutoRefreshNow]);

    // If we are rendering from a frozen snapshot (Not Refreshing mode), state updates that toggle
    // per-row UI (like opening the AnnouncementList) may not reflect because the table is reading
    // from the snapshot. This helper lets row actions patch the frozen snapshot so the UI reflects
    // user-initiated toggles while still remaining "frozen" against external updates.
    const patchFrozenSnapshotInstrumentEntity = useCallback((instrumentToken, patch) => {
        try {
            const snap = frozenSnapshotRef.current;
            if (!snap?.active || !Array.isArray(snap.stocks) || !instrumentToken) return;

            const tok = Number(instrumentToken);
            snap.stocks = snap.stocks.map(s => {
                const st = s?.instrumentEntity?.instrumentToken;
                if (Number(st) !== tok) return s;
                return {
                    ...s,
                    instrumentEntity: {
                        ...s.instrumentEntity,
                        ...(patch || {})
                    }
                };
            });
        } catch (e) {
            // never break UI due to a best-effort patch
            console.error('patchFrozenSnapshotInstrumentEntity failed', e);
        }
    }, []);

    const toggleAutoRefreshing = useCallback(() => {
        if (isAutoRefreshingRef.current) {
            // Manual pause: auto-resume after 2 minutes (as requested)
            pauseAutoRefreshForMs(2 * 60 * 1000, { triggerRefreshOnResume: true });
        } else {
            resumeAutoRefreshNow(true);
        }
    }, [pauseAutoRefreshForMs, resumeAutoRefreshNow]);

    const enableAnnouncementStocksOnly = useCallback(() => {
        // Snapshot current visible state and freeze it for 4 minutes
        const now = Date.now();

        const hasUnclearedAnnouncements = (stock) => {
            if (!stock?.instrumentEntity) return false;

            // ✅ 1) Prefer the exact same flag used by the "A" badge (set inside PortfolioRow)
            if (stock.instrumentEntity.__pfHasNewAnnouncements === true) return true;

            // ✅ 2) Fallback: check announcement maps (ONLY isNew=true)
            const rawSym = stock.instrumentEntity.tradingsymbol ?? stock.symbol ?? "";
            const normSym = normalizeSymbol(rawSym);

            const bseRaw = initialSymbolToBSEAnnouncementsMap?.[rawSym] ?? [];
            const nseRaw = initialSymbolToNSEAnnouncementsMap?.[rawSym] ?? [];

            const bseNorm = initialSymbolToBSEAnnouncementsMap?.[normSym] ?? [];
            const nseNorm = initialSymbolToNSEAnnouncementsMap?.[normSym] ?? [];

            const hasNew = (arr) => Array.isArray(arr) && arr.some(a => a && a.isNew === true);

            return (
                hasNew(bseRaw) || hasNew(nseRaw) ||
                hasNew(bseNorm) || hasNew(nseNorm)
            );
        };

        // Filter from current tab stocks (Group / client tab)
        const announcementFilteredStocks = (Array.isArray(aggregatedSortedStocksCurrentClientTab)
            ? aggregatedSortedStocksCurrentClientTab
            : []).filter(hasUnclearedAnnouncements);

        // IMPORTANT: keys MUST match calculateNewLayouts() / PortfolioTableBody lookup:
        // `${tabID}-${tab}-${token}` where tab === stock.clientId (active tab id)
        const allowedKeys = new Set(
            announcementFilteredStocks.map(s => `${tabID}-${s.clientId}-${s.instrumentEntity.instrumentToken}`)
        );

        // Prefer filtering current layouts; if layouts are not aligned, rebuild them from filtered stocks.
        let announcementFilteredLayouts = (Array.isArray(layouts) ? layouts : []).filter(l => allowedKeys.has(l.i));
        if (!announcementFilteredLayouts.length && announcementFilteredStocks.length) {
            announcementFilteredLayouts = calculateNewLayouts(announcementFilteredStocks, activeTabRef.current);
        }

        frozenSnapshotRef.current = {
            active: true,
            untilTs: now + (4 * 60 * 1000),
            stocks: announcementFilteredStocks,
            layouts: announcementFilteredLayouts
        };

        announcementStocksOnlyRef.current = true;
        setAnnouncementStocksOnly(true);

        // Freeze grid refresh for 4 minutes
        pauseAutoRefreshForMs(4 * 60 * 1000, { triggerRefreshOnResume: true });
    }, [
        aggregatedSortedStocksCurrentClientTab,
        layouts,
        pauseAutoRefreshForMs,
        initialSymbolToBSEAnnouncementsMap,
        initialSymbolToNSEAnnouncementsMap,
        tabID
    ]);

    const toggleAnnouncementStocksOnly = useCallback(() => {
        if (announcementStocksOnlyRef.current) {
            // Turn OFF and resume immediately
            resumeAutoRefreshNow(true);
        } else {
            enableAnnouncementStocksOnly();
        }
    }, [enableAnnouncementStocksOnly, resumeAutoRefreshNow]);


    const activeTabRef = useRef("Group"); // Default value
    const [pfGridrefreshKey, setPfGridrefreshKey] = useState(0);
    const [isCircleGreen, setIsCircleGreen] = useState(true); // State to hold circle color
    //const [stocks, setStocks] = useState([]);
    const [file, setFile] = useState(null);
    const [showImportPopup, setShowImportPopup] = useState(false);
    const [clients, setClients] = useState([]);
    const [showClientPopup, setShowClientPopup] = useState(false);
    const {refreshTrigger} = useRefreshContext(); // 🔹 Subscribe to global refresh
    const aggregatedStocksRef = useRef(aggregatedSortedStocksCurrentClientTab);
    const [canRenderPFRows, setCanRenderPFRows] = useState(false);
    const [shouldUseStockForDisplay, setShouldUseStockForDisplay] = useState(true);
    const hasFetchedPortfolioOnce = useRef(false);
    const [showTransactionsPopup, setShowTransactionsPopup] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [lastSortedColumn, setLastSortedColumn] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [scrollToSymbol, setScrollToSymbol] = useState(null);
    const lastSelectedSymbolRef = useRef(null);
    const [showZeroQuantity, setShowZeroQuantity] = useState(false); // default: hide zero-qty rows

    const handleFileSelect = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setShowImportPopup(false); // Close popup after file selection
        }
    };

    useEffect(() => {
        if (hasFetchedPortfolioOnce.current) {
            console.log("I was already loaded, so returning");
        }
        console.log("useEffect fetchPortfolioInstruments execution");
        if (!isDataContextLoaded || allInstrumentsList.length === 0) {
            console.log("Waiting for allInstrumentsList to load...");
            return;
        }

        fetchPortfolioInstruments().then(data => {
            //console.log('In fetchPortfolioInstruments');
            //downloadJSON(data, "PF_raw_backend_data.json");

            const mergedStocks = [];
            const instrumentCache = new Map(); // Cache to store previously found instruments
            const clientStocksMap = {}; // Store stocks separately for each client

            /*data.transactions.splice(2);*/

            const uniqueClients = [...new Set(data.transactions.map(stock => stock.clientId))];
            setClients(uniqueClients);

            // Process each TransactionDTO in data.transactions
            data.transactions.forEach(transaction => {
                // If not merger — handle normally

                const query = transaction.symbol;

                let matchedInstrument = instrumentCache.get(query) || getExactInstrument(query, allInstrumentsList);

                if (matchedInstrument) {
                    instrumentCache.set(query, matchedInstrument);
                    const combinedRow = {...transaction, instrumentEntity: matchedInstrument};

                    mergedStocks.push(combinedRow);

                    if (!clientStocksMap[transaction.clientId]) {
                        clientStocksMap[transaction.clientId] = [];
                    }
                    clientStocksMap[transaction.clientId].push(combinedRow);
                }
            });
            //downloadJSON(clientStocksMap, "PF_clientStocksMap.json");

            clientStocksMap["Group"] = mergedStocks; // ✅ Directly assign mergedStocks to "Group"

            /*Object.keys(clientStocksMap).forEach(clientId => {
                if (Array.isArray(clientStocksMap[clientId])) {
                    clientStocksMap[clientId] = clientStocksMap[clientId].slice(0, 2);
                }
            });*/

            // Extract all instrumentEntity objects from merged stocks
            const instrumentEntities = mergedStocks
                .filter(stock => stock.totalValue > 2000) // ✅ Apply filtering
                .map(stock => stock.instrumentEntity); // ✅ Extract instrumentEntity

            updateTickerSubscriptionWithKite(instrumentEntities, tabID)
                .then(response => {
                    // console.log('Subscription with Kite successful:', response.data);
                })
                .catch(error => {
                    console.error('Error in subscription with Kite:', error);
                });

            setStocksPerClient(prev => ({
                ...prev, // ✅ Preserve previous data
                ...clientStocksMap, // ✅ Store client-wise stocks, including Group
            }));
            //console.log("calling handleTabChange after fetch portfolio instruments...");
            handleTabChange(activeTabRef.current, clientStocksMap[activeTabRef.current], data.quotes);
            hasFetchedPortfolioOnce.current = true;
            //console.log("Exiting fetchPortfolioInstruments");
        });
    }, [pfGridrefreshKey, isDataContextLoaded, allInstrumentsList, refreshTrigger]);

    const isCircleGreenRef = useRef(isCircleGreen);
    useEffect(() => {
        const refreshPortfolio_Grid = () => {
            if (!isAutoRefreshingRef.current) {
                // paused
                return;
            }
            console.log(`Received WebSocket message to refresh the grid`);
            setPfGridrefreshKey(oldPfGridrefreshKey => oldPfGridrefreshKey + 1); // Refresh immediately
        };

        // Subscribe to the WebSocket service only once
        WebSocketService.subscribe('reloadStagG', null, refreshPortfolio_Grid);

        // Cleanup WebSocket subscription on component unmount
        return () => {
            console.log(`Unsubscribing from WebSocket for reloadStagG`);
            WebSocketService.unsubscribe('reloadStagG', null, refreshPortfolio_Grid);
        };
    }, []);  // Empty dependency array ensures subscription happens only once

    // Function to toggle the circle color and update the ref
    const handleCircleToggle = () => {
        setIsCircleGreen(prevState => {
            const newState = !prevState;
            isCircleGreenRef.current = newState;  // Update the ref
            return newState;
        });
        console.log("Toggling color...");

    };

    useEffect(() => {
        console.log(`showSortDropdown: ${showSortDropdown}`);
    }, [showSortDropdown]);

    // PortfolioGrid.js
    const calculateNewLayouts = (newStocks, tab) => {
        return newStocks?.map((stock, index) => ({
            i: `${tabID}-${tab}-${stock.instrumentEntity.instrumentToken}`,
            x: 0, y: index, w: 12, h: 1,
        })) || [];
    };

    React.useEffect(() => {
        if (!canRenderPFRows) {
            return;
        }
        console.log("calling handleTabChange because stocks or sortConfig is updated");
        if (!isAutoRefreshingRef.current) return;
        handleTabChange(activeTabRef.current); // ✅ Runs when stocks or sorting config changes
    }, [pfGridrefreshKey, sortConfig]); // ✅ Runs every time `stocks` or `sortConfig` change

    const sortStocks = (desiredTab, stocksForThisClient, passedPFQuotes) => {
        console.log("sortStocks called");
        if (!stocksForThisClient || Object.keys(stocksForThisClient).length === 0) return;

        let aggregatedStocks = null; // Will hold the result for the active client only

        const activeClientId = desiredTab;
        const activeClientStocks = stocksForThisClient;

        if (activeClientStocks && activeClientStocks.length > 0) {
            // ✅ Aggregate only for active client
            let sortedArray = aggregateStocks(activeClientStocks, activeClientId === "Group", activeClientId, passedPFQuotes);
            const effectiveSortConfig = {
                key: sortConfig.key ?? "currentValue",
                direction: sortConfig.direction ?? "desc"
            };

            // ✅ Apply sorting if applicable
            if (effectiveSortConfig.key !== null) {
                sortedArray.sort((a, b) => {
                    let valA = a[effectiveSortConfig.key] ?? 0;
                    let valB = b[effectiveSortConfig.key] ?? 0;

                    if (!isNaN(valA) && !isNaN(valB)) {
                        return effectiveSortConfig.direction === "asc" ? valA - valB : valB - valA;
                    } else {
                        return effectiveSortConfig.direction === "asc"
                            ? String(valA).localeCompare(String(valB))
                            : String(valB).localeCompare(String(valA));
                    }
                });
            }

            // ✅ Store result
            aggregatedStocks = sortedArray;
            console.log("sortstocks completed");
        }
        return aggregatedStocks; // ✅ Return result
    };

    const filteredStocks = useMemo(() => {
        // Always work on a safe base array
        const baseList = Array.isArray(aggregatedSortedStocksCurrentClientTab)
            ? aggregatedSortedStocksCurrentClientTab
            : [];

        // 🔹 First apply zero-quantity filter (unless user chose to show them)
        const baseAfterZeroFilter = baseList.filter(stock =>
            showZeroQuantity || (stock.netQuantity ?? 0) !== 0
        );

        // 🔹 If no search query, we’re done
        if (!searchQuery) return baseAfterZeroFilter;

        // 🔹 Apply search on top of the zero-qty filter
        const filtered = baseAfterZeroFilter.filter(stock =>
            stock.symbol.toUpperCase().includes(searchQuery)
        );

        // ✅ Always track first visible match
        if (filtered.length > 0) {
            window.__lastMatchedSymbol = filtered[0].symbol;
            setScrollToSymbol(filtered[0].symbol);
        }

        return filtered;
    }, [searchQuery, aggregatedSortedStocksCurrentClientTab, showZeroQuantity]);

    const filteredLayouts = useMemo(() => {
        return layouts.filter(layout =>
            filteredStocks.some(stock =>
                `${tabID}-${stock.clientId}-${stock.instrumentEntity.instrumentToken}` === layout.i
            )
        );
    }, [layouts, filteredStocks, tabID]);

    // -------------------------------------------------
    // Effective data shown in table (frozen vs normal)
    // -------------------------------------------------
    const effectiveSnapshot = frozenSnapshotRef.current;
    const isFrozen = !!(effectiveSnapshot?.active && (effectiveSnapshot.untilTs ?? 0) > Date.now());
    const effectiveAggregatedStocks = (isFrozen && effectiveSnapshot?.stocks)
        ? effectiveSnapshot.stocks
        : filteredStocks;
    const effectiveLayouts = (isFrozen && effectiveSnapshot?.layouts)
        ? effectiveSnapshot.layouts
        : filteredLayouts;

    // Track what's actually being shown, so we can freeze it reliably.
    useEffect(() => {
        latestDisplayStocksRef.current = effectiveAggregatedStocks;
        latestDisplayLayoutsRef.current = effectiveLayouts;
    }, [effectiveAggregatedStocks, effectiveLayouts]);

// If we paused with "freezeImmediately=false", freeze right after the next render paints.
    useEffect(() => {
        const pending = pendingFreezeRef.current;
        const snap = frozenSnapshotRef.current;

        if (!isAutoRefreshingRef.current && pending?.active && !(snap?.active)) {
            frozenSnapshotRef.current = {
                active: true,
                untilTs: pending.untilTs ?? (Date.now() + 60 * 1000),
                stocks: Array.isArray(latestDisplayStocksRef.current) ? latestDisplayStocksRef.current : [],
                layouts: Array.isArray(latestDisplayLayoutsRef.current) ? latestDisplayLayoutsRef.current : [],
            };
            pendingFreezeRef.current = { active: false, untilTs: 0 };
        }
    }, [effectiveAggregatedStocks, effectiveLayouts]);


    const aggregateStocks = (filteredStocks, isSummingAllClients = false, activeTab, passedPFQuotes) => {
        console.log("aggregateStocks started");
        const aggregated = {};

        if (pfQuotes != null && pfQuotes.length > 0) {
            passedPFQuotes = pfQuotes;
        }

        filteredStocks.forEach(stock => {
            const instrumentToken =
                stock.instrumentEntity?.instrumentToken ?? stock.instrumentToken;

            // Fallback if somehow instrumentToken is missing
            const baseKey = instrumentToken != null
                ? String(instrumentToken)
                : (stock.isin || stock.symbol);

            const key = isSummingAllClients
                ? baseKey
                : `${baseKey}-${stock.clientId}`;

            if (!aggregated[key]) {
                aggregated[key] = {
                    isin: stock.isin,
                    symbol: stock.instrumentEntity?.tradingsymbol || stock.symbol, // nice canonical display
                    clientId: activeTab,
                    instrumentEntity: stock.instrumentEntity,

                    netQuantity: stock.netQuantity || 0,
                    totalBuyPrice: (stock.netQuantity || 0) * (stock.weightedAvgPrice || 0),
                    amountInvested: (stock.netQuantity || 0) * (stock.weightedAvgPrice || 0),
                };
            } else {
                aggregated[key].netQuantity += stock.netQuantity || 0;
                aggregated[key].totalBuyPrice += (stock.netQuantity || 0) * (stock.weightedAvgPrice || 0);
                aggregated[key].amountInvested += (stock.netQuantity || 0) * (stock.weightedAvgPrice || 0);
            }
        });

        Object.values(aggregated).forEach(stock => {
            const instrumentToken = stock.instrumentEntity.instrumentToken;
            const tickAggregate = tickStorage.latest?.[instrumentToken] ?? null;
            stock.currentPrice = tickAggregate?.lastTradedPrice ?? getLastPrice(instrumentToken, passedPFQuotes);
            stock.closePrice = tickAggregate?.closePrice ?? getLastClosePrice(instrumentToken, passedPFQuotes);
            stock.weightedAvgPrice = stock.totalBuyPrice / (stock.netQuantity || 1);
            stock.currentValue = stock.netQuantity * stock.currentPrice;
            const priceDiffToday = stock.currentPrice - stock.closePrice;
            stock.todayGain = priceDiffToday * stock.netQuantity;
            stock.overallGain = stock.currentValue - stock.amountInvested;
            stock.todayGainPercentage = (priceDiffToday / (stock.closePrice || 1)) * 100 || 0;
            stock.overallGainPercentage = (stock.overallGain / (stock.amountInvested || 1)) * 100 || 0;
        });

        console.log("aggregateStocks completed");
        return Object.values(aggregated);
    };

    useEffect(() => {
        console.log("useEffect [aggregatedSortedStocksCurrentClientTab]");
        aggregatedStocksRef.current = aggregatedSortedStocksCurrentClientTab;
    }, [aggregatedSortedStocksCurrentClientTab]);


    const handleTabChange = (desiredTab, stocksForThisClient, passedPFQuotes = pfQuotes) => {
        //restStates();
        console.log("handleTabChange started");
        if (passedPFQuotes != null && Object.keys(passedPFQuotes).length > 0) {
            setPfQuotes(passedPFQuotes);  // This will be passed only once during first initialization when handleTabChange will be called after fetching instruments.
        }

        if (stocksForThisClient === null || stocksForThisClient === undefined) {
            if (stocksPerClient[desiredTab] === null || stocksPerClient[desiredTab] === undefined) {
                return;
            }
            stocksForThisClient = stocksPerClient[desiredTab];
        }
        const aggregatedSortedStocks = sortStocks(desiredTab, stocksForThisClient, passedPFQuotes);
        //setAggregatedSortedStocksPerClient below is required for popups to get the new values
        setAggregatedSortedStocksPerClient(prev => ({
            ...prev,
            [desiredTab]: aggregatedSortedStocks
        }));

        //we do setCanRenderPFRows(false); here and then setCanRenderPFRows(true); in setTimeout, so that react does two renders at 4ms interval. this is only way that setCanRenderPFRows(true); would take effect.
        // and that true would trigger useeffect relying on canRenderPFRows which would then set setShouldUseStockForDisplay(false);, but only after screen is painted with setShouldUseStockForDisplay(true); value once.
        setCanRenderPFRows(false);
        setShouldUseStockForDisplay(false);

        if (desiredTab === "Group") {
            assignPFSymbols(
                (aggregatedSortedStocks || [])
                    .filter(r => (r.netQuantity ?? 0) > 0)
                    .map(r => r.symbol ?? r.stockSymbol)
                    .filter(Boolean)
            );
        }

        setAggregatedSortedStocksCurrentClientTab(aggregatedSortedStocks);
        setLayouts(calculateNewLayouts(aggregatedSortedStocks,desiredTab));
        setCanRenderPFRows(true); // ✅ now this change will trigger useEffect

        activeTabRef.current = desiredTab;  // ✅ Update ref value
        //console.log("Updated activeTabRef:", activeTabRef.current); // ✅ Uses latest value
        console.log("handleTabChange completed");
    }

    useEffect(() => {
        if (canRenderPFRows) {
            // Use requestAnimationFrame to ensure that even when canRenderPFRows was changed (to true), let this setShouldUseStockForDisplay(false); run only after
            // screen was pinted with previous value of setShouldUseStockForDisplay (which would be false)
            requestAnimationFrame(() => {
                setShouldUseStockForDisplay(false);
            });
        }
    }, [canRenderPFRows]);

    const handleRowDoubleClick = useCallback((symbol) => {
        if (activeTabRef.current === "Group") {
            // Prepare aggregatedSortedStocksPerClient for clients not yet initialized
            clients.forEach((client) => {
                if (!aggregatedSortedStocksPerClient[client]) {
                    const stocksForThisClient = stocksPerClient[client];
                    const aggregatedSortedStocks = sortStocks(client, stocksForThisClient, pfQuotes);

                    setAggregatedSortedStocksPerClient((prev) => ({
                        ...prev,
                        [client]: aggregatedSortedStocks,
                    }));
                }
            });

            const stockDetails = stocksPerClient["Group"].filter(stock => stock.symbol === symbol);
            setSelectedStockTransactionsAcrossClients(stockDetails);
            setSelectedSymbol(symbol);
            setShowClientPopup(true);
        } else {
            setSelectedSymbol(symbol);
            setSelectedClient(activeTabRef.current);
            setShowTransactionsPopup(true);
        }
    }, [aggregatedSortedStocksPerClient, clients, pfQuotes, stocksPerClient]);

    // Sorting Function
    const handleSortClick = (columnKey) => {
        console.log("handleSortClick started");
        setSortConfig((prevConfig) => {
            if (prevConfig.key === columnKey) {
                // Toggle direction if same column
                return {
                    key: columnKey,
                    direction: prevConfig.direction === "asc" ? "desc" : "asc",
                };
            } else {
                // New column, default to desc
                return {
                    key: columnKey,
                    direction: "desc",
                };
            }
        });

        setLastSortedColumn(columnKey);
        console.log("handleSortClick completed");
    };

    return (
        <>
            {!isDataContextLoaded ? (
                <div className="pf-loading-wrapper">
                    <div className="pf-spinner"/>
                    <div className="pf-loading-text">Loading Portfolio…</div>
                </div>
            ) : (
                (() => {
                    return (
                        <div className="pf-grid-wrapper">
                            <AutoExportOnRowChange stocks={effectiveAggregatedStocks} quotes={pfQuotes} tab={activeTabRef.current}   ready={canRenderPFRows}/>
                            <div className="portfolio-grid-holder" key={tabID}>
                                <div className="client-tabs">
                                    <button type="button" onClick={() => {handleTabChange("Group");}} className={activeTabRef.current === "Group" ? "selected" : ""}>
                                        Group
                                    </button>
                                    {clients.map(clientId => (
                                        <button type="button" key={clientId} onClick={() => {handleTabChange(clientId);}} className={activeTabRef.current === clientId ? "selected" : ""}>
                                            {clientId}
                                        </button>
                                    ))}
                                </div>
                                <RowSelectionProvider
                                    stocks={aggregatedSortedStocksCurrentClientTab}
                                    lastSelectedSymbolRef={lastSelectedSymbolRef}
                                    rowHeightGridLayout={ROW_HEIGHT}
                                    /*setSearchQuery={setSearchQuery}
                                    setScrollToSymbol={setScrollToSymbol}*/
                                >
                                    <div className="pf-wrapper">
                                        <div className={`pf-table-container ${showClientPopup ? 'blurred' : ''}`}>
                                            <PortfolioHeaderTable sortConfig={sortConfig} handleSortClick={handleSortClick}/>
                                            {(!canRenderPFRows) ? (
                                                <div className="pf-loading-wrapper">
                                                    <div className="pf-spinner"/>
                                                    <div className="pf-loading-text">Loading stocks…</div>
                                                </div>
                                            ) : (
                                                (() => {
                                                    return (
                                                        <>
                                                            <PortfolioTableBody layouts={effectiveLayouts} aggregatedSortedStocksCurrentClientTab={effectiveAggregatedStocks} tabID={tabID}
                                                                                setAggregatedSortedStocksCurrentClientTab={setAggregatedSortedStocksCurrentClientTab} setLayouts={setLayouts}
                                                                                handleRowDoubleClick={handleRowDoubleClick} rowWrapperRef={rowWrapperRef}
                                                                                initialSymbolToBSEAnnouncementsMap={initialSymbolToBSEAnnouncementsMap}
                                                                                initialSymbolToNSEAnnouncementsMap={initialSymbolToNSEAnnouncementsMap}
                                                                                shouldUseStockForDisplay={shouldUseStockForDisplay}
                                                                                scrollToSymbol={scrollToSymbol} setScrollToSymbol={setScrollToSymbol} setSearchQuery={setSearchQuery}
                                                                                ROW_HEIGHT={ROW_HEIGHT}
                                                                                pauseAutoRefreshForMs={pauseAutoRefreshForMs}
                                                                                resumeAutoRefreshNow={resumeAutoRefreshNow}
                                                                                patchFrozenSnapshotInstrumentEntity={patchFrozenSnapshotInstrumentEntity}
                                                                                toggleAnnouncementStocksOnly={toggleAnnouncementStocksOnly}
                                                                                announcementStocksOnly={announcementStocksOnly}
                                                            />
                                                            {/*<PortfolioFooterTable totals={totals}/>*/}
                                                            <PortfolioTotalsProvider
                                                                aggregatedStocks={effectiveAggregatedStocks}
                                                                pfQuotes={pfQuotes}
                                                            />
                                                        </>
                                                    )
                                                }))()}
                                        </div>
                                    </div>
                                    <PortfolioUtilButtons
                                        isAutoRefreshing={isAutoRefreshing}
                                        toggleAutoRefreshing={toggleAutoRefreshing}
                                        announcementStocksOnly={announcementStocksOnly}
                                        toggleAnnouncementStocksOnly={toggleAnnouncementStocksOnly}
                                        showImportPopup={showImportPopup}
                                        setShowImportPopup={setShowImportPopup}
                                        handleFileSelect={handleFileSelect}
                                        file={file}
                                        aggregatedSortedStocksCurrentClientTab={effectiveAggregatedStocks}
                                        pfQuotes={pfQuotes}
                                        activeTabRef={activeTabRef}
                                        exportToCSV={exportToCSV}
                                        exportToExcel={exportToExcel}
                                        setSearchQuery={setSearchQuery}
                                        setScrollToSymbol={setScrollToSymbol}
                                        showZeroQuantity={showZeroQuantity}
                                        setShowZeroQuantity={setShowZeroQuantity}
                                    />
                                </RowSelectionProvider>
                                {showClientPopup && (
                                    <StockPopup
                                        selectedStockTransactionsAcrossClients={selectedStockTransactionsAcrossClients}
                                        setShowClientPopup={setShowClientPopup}
                                        aggregatedSortedStocksPerClient={aggregatedSortedStocksPerClient}
                                        symbol={selectedSymbol}/>
                                )}
                                {showTransactionsPopup && selectedClient && (
                                    <StockTransactionsPopup
                                        symbol={selectedSymbol}
                                        clientId={selectedClient}
                                        setShowTransactionsPopup={setShowTransactionsPopup}
                                    />
                                )}
                            </div>
                        </div>
                    )
                }))()}
        </>
    );

};

export default PortfolioGrid;